import { useTranslations } from "adapters/i18n";
import { type ChangeEvent, useCallback, useMemo, useState } from "react";
import type { Goal } from "shared/types";
import { LotGoalFormState } from "../lotGoal.types";
import {
  LotGoalNameError,
  validateLotGoalName,
} from "../utils/lotGoal.validators";

type UseLotGoalFormProps = {
  goals: Goal[];
  onSubmit: (formState: LotGoalFormState) => void;
  selectedGoal: Goal | null;
};

export function useLotGoalForm({
  goals,
  onSubmit,
  selectedGoal,
}: UseLotGoalFormProps) {
  const { t } = useTranslations();

  const [formState, setFormState] = useState<LotGoalFormState>({
    name: { value: selectedGoal?.name ?? "", error: null },
  });

  const nameErrorToMessage: Record<LotGoalNameError, string> = {
    [LotGoalNameError.Required]: t("common:validation.required-field"),
    [LotGoalNameError.Duplicated]: t(
      "livestock:duplicated-lot-goal-name-error"
    ),
  };

  const handleChangeName = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const { error } = validateLotGoalName(
      value,
      goals,
      selectedGoal?.id ?? null
    );
    const errorMessage = error != null ? nameErrorToMessage[error] : null;

    setFormState((prevState) => ({
      ...prevState,
      name: { value, error: errorMessage },
    }));
  };

  const handleResetForm = () => {
    setFormState({
      name: { value: "", error: null },
    });
  };

  const canSubmitForm = useMemo(() => {
    const { isValid: isNameValid } = validateLotGoalName(
      formState.name.value,
      goals,
      selectedGoal?.id ?? null
    );

    return isNameValid;
  }, [goals, formState.name.value, selectedGoal?.id]);

  const handleSubmitForm = useCallback(async () => {
    if (!canSubmitForm) {
      return null;
    }

    onSubmit(formState);
  }, [canSubmitForm, formState, onSubmit]);

  return {
    canSubmitForm,
    formState,
    handleChangeName,
    handleResetForm,
    handleSubmitForm,
  };
}
